import { useWidget } from "../../../providers/widget"
import { GalleryWidgetConfig } from "../config"
import Stylesheet from "../../../components/Stylesheet"
import { useMemo } from "react"
import GalleryStyle from "./GalleryStyle"
import FullScreenGrid from "./masonry/FullScreenGrid"
import LightBox from "./masonry/LightBox"
import WebFont from "../../../components/WebFont"
import WidgetSection from "../../../components/WidgetSection"
import ClientOnly from "../../../components/ClientOnly"
import { useFetchGalleryImages } from "../data"
import { resolveWidgetSpacingStyle } from "@homerunner-next/widget-core/spacing"
import { hostFontSizeCss } from "../../../common/host-scope"
import { useGalleryOpenAt } from "../use-open-at"

/**
 * Dark-palette override for the auto FIRST-PAINT path (#900). The base
 * Stylesheet below always emits the LIGHT (resolvedTheme-gated) accent, so light
 * output stays byte-identical; this appends the DARK accent under two selector
 * prefixes:
 *   - `:host([data-hr-theme="dark"])` — the runtime marker (explicit dark +
 *     post-hydration auto).
 *   - inside `@media (prefers-color-scheme: dark)`,
 *     `:host([data-hr-scheme="auto"]:not([data-hr-theme]))` — a server-stamped
 *     auto widget on a dark OS, painting dark AT PARSE with no JS/cookie; the
 *     `:not` drops it the instant the runtime stamps the marker at hydration.
 * Computed from `darkModeColors.accent` directly (not resolvedTheme-gated) so it
 * ships every render. `:host(...)` (not a descendant marker) is required to
 * reach the shadow host from inside its own stylesheet, and BOTH auto
 * conditions must live INSIDE `:host(...)` — a `:not` after the closing paren
 * silently never matches the host.
 */
function galleryDarkOverrides(prefix: string, darkAccent: string | undefined): string {
  if (!darkAccent) return ''
  return `
            ${prefix} .text-accent {
              color: ${darkAccent} !important;
            }
            ${prefix} .bg-accent {
              background-color: ${darkAccent} !important;
            }
          `
}

export function GalleryAppContent() {
  const { options, resolvedTheme, widgetId } = useWidget<GalleryWidgetConfig>()

  const colors = useMemo(() => {
    return resolvedTheme === 'dark' ? options.darkModeColors : options.lightModeColors;
  }, [resolvedTheme, options.lightModeColors, options.darkModeColors])

  // Section title only renders once we have actual images to show.
  // React Query dedupes with the call inside the masonry components.
  const { data } = useFetchGalleryImages(options.filter)
  const hasContent = (data?.pages?.[0]?.result?.length ?? 0) > 0

  // Other widgets (the Spaces bedroom cards, tap = tour) ask this gallery to
  // open at a photo or a room through a window event — see ../open-at.ts.
  useGalleryOpenAt()

  return (
    <div className={`homerunner-gallery-container homerunner-gallery-app w-full ${resolvedTheme === 'dark' ? 'dark' : ''}`} style={resolveWidgetSpacingStyle(options)}
    suppressHydrationWarning
    >
      {/* <ClientOnly> */}
        <WebFont />
      {/* </ClientOnly> */}
      <Stylesheet
        css={`
            .homerunner-gallery-container * {
              font-family: '${options.font.family}' !important;
            }
            ${hostFontSizeCss(widgetId, options.font.size)}
            ${colors.accent ? `
            .text-accent {
              color: ${colors.accent} !important;
            }
            .bg-accent {
              background-color: ${colors.accent} !important;
            }
            .gallery-image-container {
              border-radius: ${options.imageBorderRadius}px;
            }
            ` : ''}
            ${galleryDarkOverrides(':where(:host([data-hr-theme="dark"]))', options.darkModeColors.accent)}
            @media (prefers-color-scheme: dark) {
              ${galleryDarkOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', options.darkModeColors.accent)}
            }
            ${options.customCss ?? ''}
          `}
      />
      <GalleryStyle />
      {hasContent && (
        <WidgetSection show={options.section?.show} title={options.section?.title} />
      )}

      <div className="homerunner-gallery-fullscreen-container">
        <FullScreenGrid />
      </div>
      <div className="homerunner-gallery-fullscreen-lightbox-container">
        <LightBox />
      </div>
    </div>
  )
}
